import type { WalletOptionsResult } from "@/lib/api";

/**
 * Pure model for the create-wallet flow (ALL-4839). Everything the operator
 * does not choose is derived here from the admin-configured options returned
 * by GET /v1/wallets/new; nothing falls back to fixture data.
 */

/** Mirrors the gateway's accountAlias rule (WalletKitGatewayBackend.validatedToken). */
export const WALLET_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]*$/;
export const WALLET_NAME_MAX = 64;

export type WalletNameProblem = "empty" | "too_long" | "invalid_start" | "invalid_chars" | "double_dot";

export function walletNameProblem(raw: string): WalletNameProblem | null {
  const name = raw.trim();
  if (!name) return "empty";
  if (name.length > WALLET_NAME_MAX) return "too_long";
  if (!/^[A-Za-z0-9]/.test(name)) return "invalid_start";
  if (!WALLET_NAME_PATTERN.test(name)) return "invalid_chars";
  if (name.includes("..")) return "double_dot";
  return null;
}

export interface ChainFamily {
  /** The gateway `chain` value, e.g. "ethereum". */
  chain: string;
  /** Operator-facing family name, e.g. "EVM". */
  label: string;
  /** Short explanation of what one address covers. */
  description: string;
  /** Enabled networks the wallet's address is valid on, in API order. */
  networks: { id: string; label: string }[];
}

const FAMILY_LABELS: Record<string, { label: string; description: string }> = {
  ethereum: { label: "EVM", description: "One address, valid on every enabled EVM network" },
  bitcoin: { label: "Bitcoin", description: "UTXO addresses on the enabled Bitcoin network" },
  solana: { label: "Solana", description: "One address on the enabled Solana cluster" },
};

export function networkLabel(network: string): string {
  if (!network) return network;
  return network
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/** Groups the enabled networks by chain family. Disabled networks are dropped. */
export function chainFamilies(options: WalletOptionsResult): ChainFamily[] {
  const byChain = new Map<string, ChainFamily>();
  for (const n of options.networks) {
    if (!n.enabled || !n.chain || !n.network) continue;
    let family = byChain.get(n.chain);
    if (!family) {
      const known = FAMILY_LABELS[n.chain];
      family = {
        chain: n.chain,
        label: known?.label ?? networkLabel(n.chain),
        description: known?.description ?? "Addresses on the enabled networks below",
        networks: [],
      };
      byChain.set(n.chain, family);
    }
    if (!family.networks.some((x) => x.id === n.network)) {
      family.networks.push({ id: n.network, label: networkLabel(n.network) });
    }
  }
  return [...byChain.values()];
}

export interface AssetPreview {
  symbol: string;
  name: string;
  /** The family network the asset is configured on, e.g. "sepolia". */
  network: string;
  /** Operator-facing network label, e.g. "Sepolia". */
  networkLabel: string;
}

/**
 * The asset mapping a wallet on this chain family will be able to receive:
 * every *enabled* asset whose chain matches the family and whose network is one
 * the family derives to, in API order, deduped by symbol+network.
 *
 * Read-only by design. Creation never sends an asset (PM review, decision 1):
 * any enabled asset on the chain family can be received once the wallet exists,
 * so this is a preview of the admin-configured mapping, never a choice and never
 * a fabricated default. An empty list means the admins have not enabled any
 * asset for this family yet — the caller must say so rather than invent one.
 */
export function previewAssets(options: WalletOptionsResult, family: ChainFamily): AssetPreview[] {
  const networks = new Set(family.networks.map((n) => n.id));
  const seen = new Set<string>();
  const assets: AssetPreview[] = [];
  for (const a of options.assets) {
    if (!a.enabled || a.chain !== family.chain || !networks.has(a.network)) continue;
    const key = `${a.symbol}@${a.network}`;
    if (seen.has(key)) continue;
    seen.add(key);
    assets.push({
      symbol: a.symbol,
      name: a.name || a.symbol,
      network: a.network,
      networkLabel: networkLabel(a.network),
    });
  }
  return assets;
}

export interface ProviderRoute {
  id: string;
  name: string;
}

/** Only the routes an admin has enabled, in API order. */
export function enabledRoutes(options: WalletOptionsResult): ProviderRoute[] {
  const seen = new Set<string>();
  const routes: ProviderRoute[] = [];
  for (const p of options.providerProfiles) {
    if (!p.enabled || !p.walletProfileId || seen.has(p.walletProfileId)) continue;
    seen.add(p.walletProfileId);
    routes.push({ id: p.walletProfileId, name: p.displayName?.trim() || p.walletProfileId });
  }
  return routes;
}

/**
 * ALL-4944: the route to pre-select. The admin-configured default wins when it is an enabled,
 * listed route; otherwise the first enabled route (API order). Never a route that is not listed.
 */
export function preferredRouteId(routes: ProviderRoute[], defaultId?: string | null): string | undefined {
  if (defaultId && routes.some((r) => r.id === defaultId)) return defaultId;
  return routes[0]?.id;
}

/**
 * The create request. `network` is not an operator choice: it is the first
 * enabled network of the chosen family, sent explicitly so the gateway never
 * pairs the family with a default network from a different chain.
 */
export function createRequest(name: string, family: ChainFamily, routeId: string) {
  return {
    accountAlias: name.trim(),
    chain: family.chain,
    network: family.networks[0]?.id,
    walletProfileId: routeId,
  };
}
